import { ApiClient } from '@/config/axios.config';
import { environment } from '@/config/environment';
import type {
  PowerBIReport,
  PowerBIReportAPI,
  GenerateTokenResponse,
  GenerateTokenResponseAPI,
} from '@/types/powerBI.types';

export class PowerBIService {
  private static readonly BASE_URL = environment.powerBiApiUrl || '';

  /**
   * Map API response to client interface
   */
  private static mapAPIReport(apiReport: PowerBIReportAPI): PowerBIReport {
    return {
      id: apiReport.Id,
      name: apiReport.Name,
      type: apiReport.Type.toLowerCase() as 'report' | 'dashboard',
      tokenEndpoint: apiReport.tokenEndpoint,
    };
  }

  /**
   * Fetch basic Power BI reports
   */
  static async getReports(): Promise<PowerBIReport[]> {
    try {
      const url = `${this.BASE_URL}getreports`;
      const apiReports = await ApiClient.get<PowerBIReportAPI[]>(url);
      console.log('[PowerBIService] API response (getReports):', apiReports);
      return apiReports.map(this.mapAPIReport);
    } catch (error) {
      console.error('Failed to fetch Power BI reports:', error);
      if (!environment.production) {
        // Mock data for development
        return [];
      }
      throw error;
    }
  }

  /**
   * Fetch privileged Power BI reports
   */
  static async getReportsPrivileged(): Promise<PowerBIReport[]> {
    try {
      const url = `${this.BASE_URL}GetReportsPrivileged`;
      const apiReports = await ApiClient.get<PowerBIReportAPI[]>(url);
      console.log('[PowerBIService] API response (getReportsPrivileged):', apiReports);
      return apiReports.map((r) => ({
        ...this.mapAPIReport(r),
        tokenEndpoint: 'generateembedtokenprivileged',
      }));
    } catch (error) {
      console.error('Failed to fetch privileged Power BI reports:', error);
      if (!environment.production) {
        return [];
      }
      throw error;
    }
  }
  /**
   * Fetch KPC privileged Power BI reports
   */
  static async getReportsPrivilegedKPC(): Promise<PowerBIReport[]> {
    try {
      const url = `${this.BASE_URL}GetReportsPrivilegedKPC`;
      const apiReports = await ApiClient.get<PowerBIReportAPI[]>(url);
      console.log('[PowerBIService] API response (getReportsPrivilegedKPC):', apiReports);
      return apiReports.map((r) => ({
        ...this.mapAPIReport(r),
        tokenEndpoint: 'generateembedtokenprivilegedkpc',
      }));
    } catch (error) {
      console.error('Failed to fetch KPC Power BI reports:', error);
      if (!environment.production) {
        return [];
      }
      throw error;
    }
  }

  /**
   * Generate embed token for a specific report
   */
  static async generateEmbedToken(
    reportId: string,
    type: 'report' | 'dashboard',
    endpoint: string = 'generateembedtoken'
  ): Promise<GenerateTokenResponse> {
    try {
      const url = `${this.BASE_URL}${endpoint}`;
      const apiResponse = await ApiClient.post<GenerateTokenResponseAPI>(url, {
        reportId,
        type,
      });

      console.log('[PowerBIService] API token response:', apiResponse);

      // Map API response to client interface
      return {
        type: apiResponse.Type,
        embedReport: apiResponse.EmbedReport.map((report) => ({
          reportId: report.ReportId,
          reportName: report.ReportName,
          embedUrl: report.EmbedUrl,
        })),
        embedToken: {
          token: apiResponse.EmbedToken.Token,
          tokenId: apiResponse.EmbedToken.TokenId,
          expiration: apiResponse.EmbedToken.Expiration,
        },
      };
    } catch (error) {
      console.error('Failed to generate embed token:', error);
      throw error;
    }
  }
}
